import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { User } from '../types/auth';
import { Organization } from '../types/organization';
import DOMPurify from 'dompurify';

export class SecurityUtils {
  /**
   * Sanitizes HTML content to prevent XSS attacks
   * Uses DOMPurify to remove malicious HTML while preserving safe formatting
   * 
   * @param html - HTML string to sanitize
   * @param options - Optional DOMPurify configuration
   * @returns Sanitized HTML safe for rendering
   * 
   * @example
   * ```typescript
   * const userHTML = '<p>Hello <script>alert("XSS")</script></p>';
   * const safe = SecurityUtils.sanitizeHTML(userHTML);
   * // Result: '<p>Hello </p>'
   * ```
   */
  static sanitizeHTML(
    html: string,
    options?: {
      allowedTags?: string[];
      allowedAttributes?: string[];
      allowLinks?: boolean;
    }
  ): string {
    if (!html || typeof html !== 'string') {
      return '';
    }

    const defaultConfig: DOMPurify.Config = {
      ALLOWED_TAGS: options?.allowedTags || ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: options?.allowedAttributes || [],
      KEEP_CONTENT: true,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
    };

    // Allow links if specified
    if (options?.allowLinks) {
      defaultConfig.ALLOWED_TAGS = [...(defaultConfig.ALLOWED_TAGS || []), 'a'];
      defaultConfig.ALLOWED_ATTR = ['href', 'target', 'rel'];
      defaultConfig.ALLOWED_URI_REGEXP = /^https?:\/\//; // Only http(s) links
    }

    return DOMPurify.sanitize(html, defaultConfig);
  }

  /**
  /**
   * Validates that a user has access to a specific organization
   */
  static async validateOrganizationAccess(userId: string, organizationId: string): Promise<boolean> {
    try {
      // Get user document
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data() as User;
      
      // Check if user is member of the organization
      if (!userData.organizationIds || !userData.organizationIds.includes(organizationId)) {
        return false;
      }

      // Get organization document to verify user is in members list
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      if (!orgDoc.exists()) {
        return false;
      }

      const orgData = orgDoc.data() as Organization;
      
      // Verify user is an active member
      const member = orgData.members?.find(m => m.userId === userId);
      return member ? member.isActive : false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error validating organization access:', error instanceof Error ? error.message : 'Unknown error');
      }
      return false;
    }
  }

  /**
   * Validates that a user is an admin of a specific organization
   */
  static async validateOrganizationAdminAccess(userId: string, organizationId: string): Promise<boolean> {
    try {
      // First check basic organization access
      const hasAccess = await this.validateOrganizationAccess(userId, organizationId);
      if (!hasAccess) {
        return false;
      }

      // Get user role within organization
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      if (!orgDoc.exists()) {
        return false;
      }

      const orgData = orgDoc.data() as Organization;
      const member = orgData.members?.find(m => m.userId === userId);
      
      if (!member) {
        return false;
      }

      // Check if user has admin privileges
      return member.role === 'admin' || member.role === 'account_owner' || orgData.ownerId === userId;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error validating organization admin access:', error instanceof Error ? error.message : 'Unknown error');
      }
      return false;
    }
  }

  /**
   * Sanitizes input strings to prevent injection attacks
   */
  static sanitizeInput(input: string): string {
    if (typeof input !== 'string') {
      return '';
    }
    
    // Remove potentially dangerous characters
    return input
      .replace(/[<>]/g, '') // Remove HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .replace(/['"]/g, '') // Remove quotes that could break out of attributes
      .replace(/[\r\n\t]/g, ' ') // Replace line breaks and tabs with spaces
      .trim()
      .slice(0, 1000); // Limit length to prevent overflow attacks
  }

  /**
   * Validates and sanitizes display names
   */
  static validateDisplayName(displayName: string): { isValid: boolean; sanitized: string; error?: string } {
    if (typeof displayName !== 'string') {
      return { isValid: false, sanitized: '', error: 'Display name must be a string' };
    }

    const sanitized = this.sanitizeInput(displayName);
    
    if (!sanitized) {
      return { isValid: false, sanitized, error: 'Display name is required' };
    }

    if (sanitized.length < 2) {
      return { isValid: false, sanitized, error: 'Display name must be at least 2 characters' };
    }

    if (sanitized.length > 50) {
      return { isValid: false, sanitized, error: 'Display name must be less than 50 characters' };
    }

    // Check for only alphanumeric, spaces, hyphens, and apostrophes
    const validNameRegex = /^[a-zA-Z0-9\s\-'.]+$/;
    if (!validNameRegex.test(sanitized)) {
      return { isValid: false, sanitized, error: 'Display name contains invalid characters' };
    }

    return { isValid: true, sanitized };
  }

  /**
   * Validates password strength
   */
  static validatePassword(password: string): { isValid: boolean; error?: string } {
    if (typeof password !== 'string') {
      return { isValid: false, error: 'Password must be a string' };
    }

    if (password.length < 8) {
      return { isValid: false, error: 'Password must be at least 8 characters long' };
    }

    if (password.length > 128) {
      return { isValid: false, error: 'Password must be less than 128 characters' };
    }

    // Check for at least one lowercase, one uppercase, one digit
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);

    if (!hasLower || !hasUpper || !hasDigit) {
      return { isValid: false, error: 'Password must contain at least one uppercase letter, one lowercase letter, and one number' };
    }

    return { isValid: true };
  }

  /**
   * Validates organization names
   */
  static validateOrganizationName(name: string): { isValid: boolean; sanitized: string; error?: string } {
    if (typeof name !== 'string') {
      return { isValid: false, sanitized: '', error: 'Organization name must be a string' };
    }

    const sanitized = this.sanitizeInput(name);
    
    if (!sanitized) {
      return { isValid: false, sanitized, error: 'Organization name is required' };
    }

    if (sanitized.length < 2) {
      return { isValid: false, sanitized, error: 'Organization name must be at least 2 characters' };
    }

    if (sanitized.length > 100) {
      return { isValid: false, sanitized, error: 'Organization name must be less than 100 characters' };
    }

    return { isValid: true, sanitized };
  }

  /**
   * Validates email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254; // RFC 5321 limit
  }

  /**
   * Validates that an email belongs to the same organization domain
   * (Optional additional security - not required for basic functionality)
   */
  static isEmailFromSameOrganizationDomain(userEmail: string, invitationEmail: string): boolean {
    try {
      // For now, allow any domain - organizations can have users from different domains
      // This could be made configurable per organization in the future
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const userDomain = userEmail.split('@')[1]?.toLowerCase();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const inviteDomain = invitationEmail.split('@')[1]?.toLowerCase();
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validates UUID format for IDs
   */
  static isValidUUID(id: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return typeof id === 'string' && uuidRegex.test(id);
  }

  /**
   * Rate limiting helper - simple in-memory store for demonstration
   * In production, this should use Redis or similar
   */
  private static rateLimitStore = new Map<string, { count: number; resetTime: number }>();

  static isRateLimited(identifier: string, maxRequests: number = 10, windowMs: number = 60000): boolean {
    const now = Date.now();
    const key = identifier;
    
    const current = this.rateLimitStore.get(key);
    
    if (!current || now > current.resetTime) {
      this.rateLimitStore.set(key, { count: 1, resetTime: now + windowMs });
      return false;
    }
    
    if (current.count >= maxRequests) {
      return true;
    }
    
    current.count++;
    return false;
  }
}